/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IQueryParams, SafeAny } from '@pf/shared-common';
import { Observable } from 'rxjs';
import {
    ResourceAuth,
    WarehouseItemSettingsSearchRequestParams,
} from '@control-tower/platform-wms';
import { WarehouseItemSettingDataService } from '../infrastructure/warehouse-item-settings/warehouse-item-setting.data.service';
import {
    WarehouseItemSettingCreate,
    WarehouseItemSettingDto,
    WarehouseItemSettingRead,
    WarehouseItemSettingSearchParams,
} from '../entities/warehouse-item-setting.dto';
import { WarehouseItemSettingStore } from '../infrastructure/warehouse-item-settings/warehouse-item-setting.store';
import { WarehouseItemSettingMapper } from '../infrastructure/warehouse-item-settings/warehouse-item-setting.mapper';
import { WarehouseFacade } from './warehouse.facade';

@Injectable()
export class WarehouseItemSettingFacade extends AbstractManageEntityFacade<
    WarehouseItemSettingDto,
    WarehouseItemSettingRead,
    WarehouseItemSettingCreate,
    WarehouseItemSettingSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);

    nameField: keyof WarehouseItemSettingDto = 'id';

    constructor(
        dataService: WarehouseItemSettingDataService,
        store: WarehouseItemSettingStore,
        mapper: WarehouseItemSettingMapper
    ) {
        super(dataService, null, store, mapper);
    }

    override getDefaultSearchParams$(
        params: IQueryParams<WarehouseItemSettingDto>
    ): Observable<IQueryParams<WarehouseItemSettingDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(
            params as SafeAny
        ) as Observable<IQueryParams<WarehouseItemSettingDto>>;
    }

    override applyResourceAuth(body: WarehouseItemSettingCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof WarehouseItemSettingDto, string>> {
        return { itemId: searchText };
    }

    loadForItem$(itemId: string): Observable<WarehouseItemSettingRead[]> {
        return this.dataService.searchAll$(
            {
                pageNumber: 1,
                pageSize: 50,
                itemId,
            } as WarehouseItemSettingsSearchRequestParams,
            false
        );
    }

    upsertRowForItem$(
        body: WarehouseItemSettingCreate,
        existingSettingsId: string | null | undefined
    ): Observable<WarehouseItemSettingRead> {
        this.applyResourceAuth(body);
        if (existingSettingsId) {
            return (
                this.dataService as WarehouseItemSettingDataService
            ).updatePut$(existingSettingsId, body);
        }
        return this.dataService.create$(body);
    }

    deleteBySettingsId$(
        settingsId: string
    ): Observable<WarehouseItemSettingRead> {
        return (this.dataService as WarehouseItemSettingDataService).delete$(
            settingsId
        );
    }
}
